import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Phone, Calendar, CheckCircle2, ShieldCheck, Truck, ChevronRight } from 'lucide-react';
export function Home() {
  return <div className="bg-white" data-id="element-546">
      {/* HERO */}
      <section className="bg-white py-16 lg:py-24 overflow-hidden" data-id="element-547">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-548">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-549">
            {/* Left Column - Text Content */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} className="text-left" data-id="element-550">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-black tracking-tight mb-6" data-id="element-551">
                Safe, Caring &amp; Affordable{' '}
                <span className="text-primary-600 block mt-2" data-id="element-552">
                  Wheelchair Transportation
                </span>
              </h1>

              <p className="text-lg text-warm-700 mb-8 max-w-xl leading-relaxed" data-id="element-553">
                Wheelchair transportation and accessible taxi service available
                24/7 for hospitals, airports &amp; daily rides. Book your ride
                today.
              </p>

              <div className="flex flex-col gap-4 max-w-md" data-id="element-554">
                <a href="tel:5551234567" className="w-full" data-id="element-555">
                  <Button size="lg" className="w-full bg-black hover:bg-warm-800 text-white py-4 text-lg justify-center" data-id="element-556">
                    <Phone className="w-5 h-5 mr-3" data-id="element-557" />
                    +1 (555) 123-4567
                  </Button>
                </a>
                <Link to="/booking" className="w-full" data-id="element-558">
                  <Button size="lg" variant="primary" className="w-full py-4 text-lg justify-center" data-id="element-559">
                    Start My Reservation
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right Column - Image */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative mt-10 lg:mt-0 px-4 pb-8" data-id="element-560">
              <div className="relative" data-id="element-561">
                <img src="https://images.pexels.com/photos/6646918/pexels-photo-6646918.jpeg?auto=compress&cs=tinysrgb&w=1000" alt="Wheelchair accessible vehicle" className="w-full h-[350px] md:h-[450px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-562" />
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-563">
                  <Phone className="w-5 h-5 fill-current" data-id="element-564" />
                  +1 (555) 123-4567
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ACCESSIBLE TAXI SERVICE */}
      <section className="bg-warm-50 py-16 lg:py-24 overflow-hidden" data-id="element-565">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-566">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-567">
            {/* Left Column - Image */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative px-4 pb-8" data-id="element-568">
              <div className="relative" data-id="element-569">
                <img src="https://images.unsplash.com/photo-1559416523-140ddc3d238c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Accessible taxi service vehicle" className="w-full h-[350px] md:h-[450px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-570" />
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-571">
                  <Phone className="w-5 h-5 fill-current" data-id="element-572" />
                  +1 (555) 123-4567
                </div>
              </div>
            </motion.div>

            {/* Right Column - Text Content */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-left" data-id="element-573">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-4 tracking-tight" data-id="element-574">
                Your Trusted{' '}
                <span className="text-primary-600" data-id="element-575">
                  Accessible Taxi Service
                </span>
              </h2>

              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-576">
                Whether it's a medical appointment, airport transfer, or a
                family visit — our door-through-door service ensures you travel
                with dignity, comfort, and complete peace of mind.
              </p>

              <p className="text-warm-700 mb-6 font-medium" data-id="element-577">
                What makes our taxi service stand out:
              </p>

              <ul className="space-y-4 mb-8" data-id="element-578">
                {[{
                title: 'Door-Through-Door Assistance',
                desc: 'We help from your front door to the reception desk of your destination.'
              }, {
                title: 'All Trip Types Welcome',
                desc: 'Medical visits, airport runs, shopping trips, family gatherings, and more.'
              }, {
                title: 'Wheelchair & Stretcher Ready',
                desc: 'Vehicles equipped with ramps, lifts, and secure tie-down systems.'
              }, {
                title: 'Same-Day Booking Available',
                desc: "Need a ride today? Call us and we'll get you there."
              }, {
                title: 'Flat-Rate Pricing',
                desc: 'Know your fare upfront — no surprises, no hidden charges.'
              }].map((item, i) => <li key={i} className="flex items-start" data-id="element-579">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-580"></div>
                    <p className="text-warm-700" data-id="element-581">
                      <strong className="text-black" data-id="element-582">{item.title}:</strong>{' '}
                      {item.desc}
                    </p>
                  </li>)}
              </ul>

              <Link to="/booking" className="inline-block w-full max-w-md" data-id="element-583">
                <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-584">
                  <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-585" />
                  Book Your Ride Now
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 lg:py-24 bg-white" data-id="element-586">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-587">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-588">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-2 lg:order-1" data-id="element-589">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-4 tracking-tight" data-id="element-590">
                Why Choose{' '}
                <span className="text-primary-600" data-id="element-591">Gozie Transportation?</span>
              </h2>

              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-592">
                Choosing the right wheelchair taxi service is about more than
                just getting from point A to point B. It's about trust, comfort,
                and knowing your needs will be met without compromise.
              </p>

              <p className="text-warm-700 mb-6 font-medium" data-id="element-593">
                Here's why thousands choose us every year:
              </p>

              <ul className="space-y-4 mb-8" data-id="element-594">
                {[{
                title: 'Specialized Fleet',
                desc: 'Every vehicle is fully wheelchair accessible with secure locking systems and ramps.'
              }, {
                title: 'Experienced Drivers',
                desc: 'Trained in assisting passengers with disabilities and mobility challenges.'
              }, {
                title: 'On-Time Guarantee',
                desc: 'We understand how important punctuality is, especially for medical appointments.'
              }, {
                title: 'Affordable Rates',
                desc: 'Transparent pricing with no hidden fees.'
              }, {
                title: '24/7 Availability',
                desc: "Day or night, we're just a call away."
              }].map((item, i) => <li key={i} className="flex items-start" data-id="element-595">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-596"></div>
                    <p className="text-warm-700" data-id="element-597">
                      <strong className="text-black" data-id="element-598">{item.title}:</strong>{' '}
                      {item.desc}
                    </p>
                  </li>)}
              </ul>

              <Link to="/booking" className="inline-block w-full max-w-md" data-id="element-599">
                <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-600">
                  <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-601" />
                  Reserve Taxi
                </Button>
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="order-1 lg:order-2 relative px-4 pb-8" data-id="element-602">
              <div className="relative" data-id="element-603">
                <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Experienced driver in accessible vehicle" className="w-full h-[350px] md:h-[450px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-604" />
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-605">
                  <Phone className="w-5 h-5 fill-current" data-id="element-606" />
                  +1 (555) 123-4567
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* OUR COMMITMENT TO SAFETY */}
      <section className="py-16 lg:py-24 bg-warm-50" data-id="element-607">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-608">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-609">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="order-1 lg:order-1 relative px-4 pb-8" data-id="element-610">
              <div className="relative" data-id="element-611">
                <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Professional driver performing vehicle safety inspection" className="w-full h-[350px] md:h-[450px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-612" />
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-613">
                  <Phone className="w-5 h-5 fill-current" data-id="element-614" />
                  +1 (555) 123-4567
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-2 lg:order-2" data-id="element-615">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-4 tracking-tight" data-id="element-616">
                Our Commitment to{' '}
                <span className="text-primary-600" data-id="element-617">Passenger Safety</span>
              </h2>

              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-618">
                Safety isn't just a policy — it's the foundation of everything
                we do. From rigorous vehicle inspections to comprehensive driver
                training, every detail is designed to protect you and your loved
                ones.
              </p>

              <p className="text-warm-700 mb-6 font-medium" data-id="element-619">
                Our safety standards exceed industry requirements:
              </p>

              <ul className="space-y-4 mb-8" data-id="element-620">
                {[{
                title: 'Certified Drivers',
                desc: 'Every driver holds CPR, First Aid, and PASS (Passenger Assistance Safety and Sensitivity) certification.'
              }, {
                title: 'Daily Vehicle Inspections',
                desc: 'Each vehicle undergoes a thorough safety check before every shift, including wheelchair securement systems.'
              }, {
                title: 'GPS-Tracked Fleet',
                desc: 'Real-time tracking on every vehicle so dispatchers and families can monitor rides.'
              }, {
                title: 'Full Insurance Coverage',
                desc: 'Comprehensive commercial insurance covering all passengers and mobility equipment.'
              }, {
                title: 'Background-Checked Staff',
                desc: 'Rigorous background checks and regular drug screening for every team member.'
              }].map((item, i) => <li key={i} className="flex items-start" data-id="element-621">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-622"></div>
                    <p className="text-warm-700" data-id="element-623">
                      <strong className="text-black" data-id="element-624">{item.title}:</strong>{' '}
                      {item.desc}
                    </p>
                  </li>)}
              </ul>

              <Link to="/about" className="inline-block w-full max-w-md" data-id="element-625">
                <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-626">
                  <ShieldCheck className="w-5 h-5 mr-2" data-id="element-627" />
                  Learn About Our Safety Standards
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVING OUR COMMUNITY */}
      <section className="py-16 lg:py-24 bg-white" data-id="element-628">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-629">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-630">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-2 lg:order-1" data-id="element-631">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-4 tracking-tight" data-id="element-632">
                Proudly Serving{' '}
                <span className="text-primary-600" data-id="element-633">Our Community</span>
              </h2>

              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-634">
                For over 15 years, Gozie Transportation has been a trusted
                partner for hospitals, rehabilitation centers, senior living
                communities, and families across the metropolitan area. We
                believe mobility is a fundamental right.
              </p>

              <p className="text-warm-700 mb-6 font-medium" data-id="element-635">
                Our community impact speaks for itself:
              </p>

              <ul className="space-y-4 mb-8" data-id="element-636">
                {[{
                title: 'Hospital Partnerships',
                desc: 'Contracted with leading hospitals and dialysis centers for reliable patient transport.'
              }, {
                title: 'Senior Living Facilities',
                desc: 'Dedicated service agreements with assisted living and nursing home communities.'
              }, {
                title: 'Insurance & Medicaid Accepted',
                desc: 'We work directly with insurance providers and accept Medicaid for qualifying rides.'
              }, {
                title: 'Wide Coverage Area',
                desc: 'Serving the entire metropolitan area including suburbs, airports, and neighboring counties.'
              }, {
                title: 'Multilingual Drivers',
                desc: 'Our diverse team speaks multiple languages to serve all members of our community.'
              }].map((item, i) => <li key={i} className="flex items-start" data-id="element-637">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-638"></div>
                    <p className="text-warm-700" data-id="element-639">
                      <strong className="text-black" data-id="element-640">{item.title}:</strong>{' '}
                      {item.desc}
                    </p>
                  </li>)}
              </ul>

              <Link to="/service-area" className="inline-block w-full max-w-md" data-id="element-641">
                <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-642">
                  <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-643" />
                  View Our Service Area
                </Button>
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="order-1 lg:order-2 relative px-4 pb-8" data-id="element-644">
              <div className="relative" data-id="element-645">
                <img src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Community members and transportation team" className="w-full h-[350px] md:h-[450px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-646" />
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-647">
                  <Phone className="w-5 h-5 fill-current" data-id="element-648" />
                  +1 (555) 123-4567
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BRAND BANNER */}
      <section className="bg-primary-600 py-16" data-id="element-649">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-650">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-651">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6" data-id="element-652">
              Our Specialized Wheelchair Transportation Services
            </h2>
            <p className="text-primary-50 text-lg leading-relaxed max-w-4xl mx-auto" data-id="element-653">
              Explore our comprehensive wheelchair transportation services,
              designed for seniors, disabled passengers, families, caregivers,
              and medical facilities. Every service is provided with
              ADA-compliant vehicles, trained drivers, and a focus on safety,
              comfort, and reliability. Whether it's daily rides, medical
              appointments, or special events, we have the right service for
              you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SERVICE 1: Wheelchair Transportation */}
      <section className="py-16 lg:py-24 bg-white" data-id="element-654">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-655">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-656">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-2 lg:order-1" data-id="element-657">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-6" data-id="element-658">
                Wheelchair Transportation{' '}
                <span className="text-primary-600" data-id="element-659">Services</span>
              </h2>
              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-660">
                Gozie Transportation provides safe and reliable wheelchair
                transportation. We cater to seniors, individuals with
                disabilities, and anyone who needs accessible mobility. Our
                service ensures comfort, punctuality, and professional support
                every step of the way.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-661">
                {['Door-to-door service across all boroughs', 'ADA-compliant vehicles with secure wheelchair tie-downs', 'Trained drivers for safe and respectful assistance', 'Flexible scheduling for medical, personal, or daily travel'].map((item, i) => <li key={i} className="flex items-start" data-id="element-662">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-663"></div>
                    <p className="text-warm-700" data-id="element-664">{item}</p>
                  </li>)}
              </ul>
              <Link to="/services" className="inline-flex items-center text-primary-600 font-bold hover:text-primary-700 mb-8 transition-colors" data-id="element-665">
                Learn More About Wheelchair Transportation{' '}
                <ChevronRight className="w-5 h-5 ml-1" data-id="element-666" />
              </Link>
              <div className="block" data-id="element-667">
                <Link to="/booking" className="inline-block w-full max-w-md" data-id="element-668">
                  <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-669">
                    <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-670" />
                    Take Reliable Service
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="order-1 lg:order-2 relative px-4 pb-8" data-id="element-671">
              <div className="relative" data-id="element-672">
                <img src="https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Wheelchair transportation service" className="w-full h-[300px] md:h-[400px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-673" />
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-674">
                  <Phone className="w-5 h-5 fill-current" data-id="element-675" />
                  +1 (555) 123-4567
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICE 2: Airport Wheelchair Assistance */}
      <section className="py-16 lg:py-24 bg-primary-900 text-white" data-id="element-676">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-677">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-678">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="order-1 lg:order-1 relative px-4 pb-8" data-id="element-679">
              <div className="relative" data-id="element-680">
                <img src="https://images.unsplash.com/photo-1529074963764-98f45c47344b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Airport wheelchair assistance" className="w-full h-[300px] md:h-[400px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-white shadow-2xl" data-id="element-681" />
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-682">
                  <Phone className="w-5 h-5 fill-current" data-id="element-683" />
                  +1 (555) 123-4567
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-2 lg:order-2" data-id="element-684">
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6" data-id="element-685">
                Airport Wheelchair{' '}
                <span className="text-primary-300" data-id="element-686">Assistance</span>
              </h2>
              <p className="text-primary-50 mb-6 text-lg leading-relaxed" data-id="element-687">
                We provide expert wheelchair assistance to and from all major
                airports. Our service includes curbside help, luggage handling,
                and timely pickups for smooth airport travel. Seniors, disabled
                passengers, and medical travelers can rely on our experienced
                team.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-688">
                {['Curb-to-gate assistance for arrivals and departures', 'ADA-accessible vehicles with professional staff', 'Flight tracking and flexible timing adjustments', 'Luggage handling and caregiver support included'].map((item, i) => <li key={i} className="flex items-start" data-id="element-689">
                    <div className="w-1.5 h-1.5 rounded-full bg-white mt-2.5 mr-3 flex-shrink-0" data-id="element-690"></div>
                    <p className="text-primary-50" data-id="element-691">{item}</p>
                  </li>)}
              </ul>
              <Link to="/services" className="inline-flex items-center text-white font-bold hover:text-primary-200 mb-8 transition-colors" data-id="element-692">
                Learn More About Airport Wheelchair Assistance{' '}
                <ChevronRight className="w-5 h-5 ml-1" data-id="element-693" />
              </Link>
              <div className="block" data-id="element-694">
                <Link to="/booking" className="inline-block w-full max-w-md" data-id="element-695">
                  <Button size="lg" variant="outline" className="w-full bg-white text-primary-900 border-white hover:bg-warm-100 hover:text-primary-900 justify-center py-4 text-lg" data-id="element-696">
                    <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-697" />
                    Take Reliable Service
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICE 3: NEMT */}
      <section className="py-16 lg:py-24 bg-white" data-id="element-698">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-699">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-700">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-2 lg:order-1" data-id="element-701">
              <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-6" data-id="element-702">
                Non-Emergency Medical{' '}
                <span className="text-primary-600 block" data-id="element-703">
                  Transportation (NEMT)
                </span>
              </h2>
              <p className="text-warm-700 mb-6 text-lg leading-relaxed" data-id="element-704">
                Our NEMT service provides safe, punctual transportation for
                medical appointments, hospital visits, and rehabilitation. We
                cater to seniors, post-surgery patients, and anyone requiring
                mobility assistance. Each ride ensures comfort, safety, and
                reliable scheduling.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-705">
                {['Door-to-door transport to hospitals, clinics, and rehab centers', 'ADA-compliant vans with trained drivers', 'Caregiver assistance provided free when required', 'Flexible scheduling to match appointments and treatment times'].map((item, i) => <li key={i} className="flex items-start" data-id="element-706">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600 mt-2.5 mr-3 flex-shrink-0" data-id="element-707"></div>
                    <p className="text-warm-700" data-id="element-708">{item}</p>
                  </li>)}
              </ul>
              <Link to="/services" className="inline-flex items-center text-primary-600 font-bold hover:text-primary-700 mb-8 transition-colors" data-id="element-709">
                Learn More About NEMT <ChevronRight className="w-5 h-5 ml-1" data-id="element-710" />
              </Link>
              <div className="block" data-id="element-711">
                <Link to="/booking" className="inline-block w-full max-w-md" data-id="element-712">
                  <Button size="lg" variant="primary" className="w-full justify-center py-4 text-lg" data-id="element-713">
                    <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-714" />
                    Take Reliable Service
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="order-1 lg:order-2 relative px-4 pb-8" data-id="element-715">
              <div className="relative" data-id="element-716">
                <img src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Non-emergency medical transportation" className="w-full h-[300px] md:h-[400px] object-cover rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-xl rounded-bl-xl border-4 border-primary-600 shadow-2xl" data-id="element-717" />
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white border border-warm-200 shadow-lg rounded-lg px-6 py-3 flex items-center gap-3 text-primary-600 font-bold text-lg md:text-xl whitespace-nowrap" data-id="element-718">
                  <Phone className="w-5 h-5 fill-current" data-id="element-719" />
                  +1 (555) 123-4567
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS (Kept exactly as is) */}
      <section className="py-16 lg:py-24 bg-warm-50" data-id="element-720">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-721">
          <div className="text-center mb-14" data-id="element-722">
            <h2 className="text-3xl md:text-4xl font-extrabold text-black mb-3" data-id="element-723">
              How It Works
            </h2>
            <p className="text-warm-600 text-lg" data-id="element-724">
              Three simple steps to your ride.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-725">
            {[{
            step: '1',
            icon: <Calendar className="w-7 h-7" data-id="element-726" />,
            title: 'Schedule',
            desc: 'Book online or call our 24/7 dispatch team.'
          }, {
            step: '2',
            icon: <Truck className="w-7 h-7" data-id="element-727" />,
            title: 'We Arrive',
            desc: 'A trained driver arrives on time and assists you.'
          }, {
            step: '3',
            icon: <ShieldCheck className="w-7 h-7" data-id="element-728" />,
            title: 'Travel Safely',
            desc: 'Enjoy a comfortable, secure ride to your destination.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="text-center p-8 rounded-2xl border border-warm-200 bg-white" data-id="element-729">
                <div className="w-12 h-12 mx-auto bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-lg mb-5" data-id="element-730">
                  {item.step}
                </div>
                <div className="w-14 h-14 mx-auto bg-primary-50 text-primary-600 rounded-xl flex items-center justify-center mb-5" data-id="element-731">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-black mb-2" data-id="element-732">
                  {item.title}
                </h3>
                <p className="text-warm-600" data-id="element-733">{item.desc}</p>
              </motion.div>)}
          </div>

          <div className="mt-10 text-center" data-id="element-734">
            <a href="tel:5551234567" className="inline-flex items-center justify-center px-6 py-3 bg-black hover:bg-warm-800 text-white font-bold rounded-lg transition-colors" data-id="element-735">
              <Phone className="w-5 h-5 mr-2" data-id="element-736" />
              Call (555) 123-4567
            </a>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA (Kept exactly as is) */}
      <section className="py-16 bg-black" data-id="element-737">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-738">
          <h2 className="text-3xl font-extrabold text-white mb-4" data-id="element-739">
            Ready to Book Your Ride?
          </h2>
          <p className="text-warm-400 text-lg mb-8" data-id="element-740">
            Our dispatch team is available around the clock.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-741">
            <Link to="/booking" data-id="element-742">
              <Button size="lg" variant="primary" data-id="element-743">
                Book Online
              </Button>
            </Link>
            <a href="tel:5551234567" data-id="element-744">
              <Button size="lg" className="!bg-white !text-amber-900 hover:!bg-warm-100 border-2 border-white hover:border-amber-900" data-id="element-745">
                <Phone className="w-5 h-5 mr-2" data-id="element-746" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>;
}